import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import type { GpcChatWorkflowStageKey } from '../components/GpcChat/types';
import HomeChatLauncher from '../components/HomeChat/HomeChatLauncher';
import type { HomeChatMode } from '../components/HomeChat/homeChatMode';
import { getWorkflowOnlyPresentation } from '../components/HomeChat/workflowOnlyPresentation';
import type {
  HomeChatHandoffContext,
  WorkflowRecommendationType,
} from '../components/HomeChat/types';
import RecentWorkflows from '../components/RecentWorkflows';
import WorkflowTypeGrid, {
  type WorkflowType,
} from '../components/WorkflowTypeGrid';
import HomepageTour, {
  type HomepageTourHandle,
} from '../components/tour/HomepageTour';
import { MOBILE_DENSITY } from '../theme/mobileDensity';

interface WorkflowTypeSelectorPageProps {
  canAccessRaTraining?: boolean;
  onOpenRaTraining?: () => void;
  canAccessTraining?: boolean;
  onOpenTrainingTile?: () => void;
  onOpenTrainingStarter?: () => void;
  onOpenTrainingSuggestion?: () => void;
  onSelect: (type: WorkflowType) => void;
  allowedWorkflowTypes?: string[] | null;
  canUseChatbot?: boolean;
  homeChatMode?: HomeChatMode;
  workflowOnlyAudience?: 'ra' | 'ce' | null;
  /** Presentation-only previews may show a constrained shell without entering it. */
  readOnly?: boolean;
  examplesAccessScope?: string;
  allowDcmaPbp?: boolean;
  allowDcmaCrr?: boolean;
  marketResearchCustomerView?: boolean;
  onOpenWorkflow?: (
    flowId: string,
    targetStageHint?: GpcChatWorkflowStageKey,
    context?: HomeChatHandoffContext,
  ) => void;
  onOpenMrWorkflow?: (
    flowId: string,
    context?: HomeChatHandoffContext,
  ) => void;
  onOpenConstructionSowWorkflow?: (
    flowId: string,
    context?: HomeChatHandoffContext,
  ) => void;
  onOpenGeneratedServicesPwsWorkflow?: (
    flowId: string,
    context?: HomeChatHandoffContext,
  ) => void;
  onOpenNavigationHandle?: (
    navigationHandle: string,
    context?: HomeChatHandoffContext,
  ) => Promise<void>;
  onOpenCreatedWorkflow?: (
    flowId: string,
    context?: HomeChatHandoffContext,
  ) => Promise<void>;
  onStartWorkflowType?: (
    workflowType: WorkflowRecommendationType,
    context?: HomeChatHandoffContext,
  ) => void;
  onOpenRecentWorkflow?: (
    flowId: string,
    flowName: string,
    workflowType?: WorkflowType,
  ) => void;
  onViewAllWorkflows?: () => void;
  onOpenWorkflowNotifications?: (flowId: string, flowName: string) => void;
  userEmail?: string | null;
  onFeedbackSourceInteraction?: (
    area: 'chat' | 'recent_workflows' | 'workflow_picker',
    confidence: 'high' | 'medium',
  ) => void;
}

export interface WorkflowTypeSelectorPageHandle {
  resetToCategories: () => void;
}

function formatNameFromEmail(email?: string | null): string {
  if (!email) return '';
  const firstSegment = (email.split('@')[0] ?? '').split('.')[0] ?? '';
  return firstSegment
    ? firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1).toLowerCase()
    : '';
}

const WorkflowTypeSelectorPage = forwardRef<
  WorkflowTypeSelectorPageHandle,
  WorkflowTypeSelectorPageProps
>(({
  canAccessRaTraining = false,
  onOpenRaTraining,
  canAccessTraining = false,
  onOpenTrainingTile,
  onOpenTrainingStarter,
  onOpenTrainingSuggestion,
  onSelect,
  allowedWorkflowTypes,
  canUseChatbot = true,
  homeChatMode = 'none',
  workflowOnlyAudience = null,
  readOnly = false,
  examplesAccessScope,
  allowDcmaPbp = false,
  allowDcmaCrr = false,
  marketResearchCustomerView = false,
  onOpenWorkflow,
  onOpenMrWorkflow,
  onOpenConstructionSowWorkflow,
  onOpenGeneratedServicesPwsWorkflow,
  onOpenNavigationHandle,
  onOpenCreatedWorkflow,
  onStartWorkflowType,
  onOpenRecentWorkflow,
  onViewAllWorkflows,
  onOpenWorkflowNotifications,
  userEmail,
  onFeedbackSourceInteraction,
}, ref) => {
  const tourRef = useRef<HomepageTourHandle>(null);
  const greetingName = formatNameFromEmail(userEmail);
  const hasWorkflowOnlyShell = homeChatMode === 'workflow_only'
    && getWorkflowOnlyPresentation(allowedWorkflowTypes, workflowOnlyAudience) !== null;

  useImperativeHandle(ref, () => ({
    resetToCategories: () => {
      window.dispatchEvent(new Event('home-chat:focus'));
    },
  }), []);

  if (!canUseChatbot && !hasWorkflowOnlyShell) {
    return (
      <>
        <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2 }, pt: { xs: 2, sm: 3, md: 5 }, pb: { xs: 2, sm: 3 } }}>
          <Box
            data-tour-id="home-tour-title"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              mb: { xs: 1, sm: 2 },
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            <Box>
              <Typography component="h1" variant="h2">
                Choose a workflow
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                Start a new workflow or continue saved work from the workflow manager.
              </Typography>
            </Box>
            <Button
              onClick={() => tourRef.current?.replay()}
              startIcon={<LightbulbOutlinedIcon />}
              variant="text"
              size="small"
              sx={{ flexShrink: 0 }}
            >
              Take a tour
            </Button>
          </Box>
          <WorkflowTypeGrid
            onSelect={(type) => {
              onFeedbackSourceInteraction?.('workflow_picker', 'high');
              onSelect(type);
            }}
            allowedWorkflowTypes={allowedWorkflowTypes}
            allowDcmaPbp={allowDcmaPbp}
            allowDcmaCrr={allowDcmaCrr}
            marketResearchCustomerView={marketResearchCustomerView}
          />
          {(canAccessTraining || canAccessRaTraining) && onOpenRecentWorkflow && onViewAllWorkflows && (
            <Box sx={{ mt: { xs: MOBILE_DENSITY.sectionGap, sm: 4 } }}>
              <RecentWorkflows canAccessTraining={canAccessTraining} canAccessRaTraining={canAccessRaTraining} onOpenRaTraining={onOpenRaTraining} onOpenTraining={onOpenTrainingTile} allowedWorkflowTypes={allowedWorkflowTypes} canUseDcmaPbp={allowDcmaPbp} canUseDcmaCrr={allowDcmaCrr} onOpen={onOpenRecentWorkflow} onViewAll={onViewAllWorkflows} onOpenNotifications={onOpenWorkflowNotifications} />
            </Box>
          )}
        </Container>
        <HomepageTour ref={tourRef} canUseChatbot={false} />
      </>
    );
  }

  return (
    <>
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2 }, pt: 0.5, pb: { xs: 1, sm: 2 } }}>
        <Box
          data-tour-id="home-tour-title"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
            alignItems: 'center',
            gap: { xs: 0.5, md: 0 },
            mb: 0.25,
          }}
        >
          <Typography
            component="h1"
            variant="h3"
            sx={(theme) => ({
              gridColumn: { xs: 1, md: 2 },
              textAlign: 'center',
              color: theme.palette.mode === 'light' ? theme.palette.common.black : theme.palette.text.primary,
            })}
          >
            {greetingName ? `Welcome back, ${greetingName}` : 'Welcome back'}
          </Typography>
          <Button
            onClick={() => tourRef.current?.replay()}
            startIcon={<LightbulbOutlinedIcon />}
            variant="text"
            size="small"
            sx={{ gridColumn: { xs: 1, md: 3 }, justifySelf: 'end' }}
          >
            Take a tour
          </Button>
        </Box>

        {onOpenRecentWorkflow && onViewAllWorkflows && (
          <Box
            data-tour-id="home-tour-recent"
            sx={{ mb: { xs: 1, sm: 1.5 } }}
            onPointerDownCapture={() => onFeedbackSourceInteraction?.('recent_workflows', 'high')}
            onClickCapture={() => onFeedbackSourceInteraction?.('recent_workflows', 'high')}
            onFocusCapture={(event) => {
              if (event.nativeEvent.isTrusted) {
                onFeedbackSourceInteraction?.('recent_workflows', 'medium');
              }
            }}
          >
            <RecentWorkflows
              canAccessRaTraining={canAccessRaTraining}
              onOpenRaTraining={onOpenRaTraining}
              canAccessTraining={canAccessTraining}
              onOpenTraining={onOpenTrainingTile}
              allowedWorkflowTypes={allowedWorkflowTypes}
              canUseDcmaPbp={allowDcmaPbp} canUseDcmaCrr={allowDcmaCrr}
              onOpen={onOpenRecentWorkflow}
              onViewAll={onViewAllWorkflows}
              onOpenNotifications={onOpenWorkflowNotifications}
            />
          </Box>
        )}

        <Paper
          data-tour-id="home-chat-launcher"
          variant="outlined"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            p: { xs: 1, sm: 2.5, md: 3 },
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.28),
            boxShadow: (theme) => (
              `0 18px 48px ${alpha(theme.palette.primary.main, 0.10)}`
            ),
            bgcolor: 'background.paper',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: '0 0 auto 0',
              height: 4,
              bgcolor: 'primary.main',
            },
            '& [data-testid="home-chat-search-bar"]': {
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              bgcolor: 'background.paper',
              boxShadow: 2,
              overflow: 'hidden',
              transition: 'border-color 180ms ease, box-shadow 180ms ease',
            },
            '& [data-testid="home-chat-search-bar"]:focus-within': {
              borderColor: 'primary.main',
              boxShadow: (theme) => (
                `0 0 0 4px ${alpha(theme.palette.primary.main, 0.14)}`
              ),
            },
            '& [data-testid="home-chat-search-bar"] > div': {
              borderTop: 'none',
              minHeight: { xs: 56, sm: 64 },
              px: { xs: 1, sm: 1.5 },
              py: { xs: 0.75, sm: 1.25 },
            },
            '& [data-testid="home-chat-search-bar"] textarea': {
              border: 'none',
              fontSize: '1rem',
              lineHeight: 1.5,
            },
          }}
          onPointerDownCapture={() => onFeedbackSourceInteraction?.('chat', 'high')}
          onClickCapture={() => onFeedbackSourceInteraction?.('chat', 'high')}
          onInputCapture={() => onFeedbackSourceInteraction?.('chat', 'high')}
          onFocusCapture={(event) => {
            if (event.nativeEvent.isTrusted) {
              onFeedbackSourceInteraction?.('chat', 'medium');
            }
          }}
        >
          <HomeChatLauncher
            examplesAccessScope={examplesAccessScope}
            onOpenExampleWorkflow={(id, title, type) => onOpenRecentWorkflow?.(id, title, type as WorkflowType)}
            canAccessTraining={canAccessTraining}
            onOpenTraining={onOpenTrainingStarter}
            onOpenTrainingSuggestion={onOpenTrainingSuggestion}
            canUseChatbot={canUseChatbot}
            homeChatMode={homeChatMode}
            workflowOnlyAudience={workflowOnlyAudience}
            readOnly={readOnly}
            onOpenWorkflow={onOpenWorkflow}
            onOpenMrWorkflow={onOpenMrWorkflow}
            onOpenConstructionSowWorkflow={onOpenConstructionSowWorkflow}
            onOpenGeneratedServicesPwsWorkflow={onOpenGeneratedServicesPwsWorkflow}
            onOpenNavigationHandle={onOpenNavigationHandle}
            onOpenCreatedWorkflow={onOpenCreatedWorkflow}
            onStartWorkflowType={onStartWorkflowType}
            allowedWorkflowTypes={allowedWorkflowTypes}
          />
        </Paper>
      </Container>
      <HomepageTour ref={tourRef} canUseChatbot />
    </>
  );
});

WorkflowTypeSelectorPage.displayName = 'WorkflowTypeSelectorPage';

export default WorkflowTypeSelectorPage;
